import toast, {ToastType} from '@/components/Toast';
import {WizDocument} from '@/interface';
import classNames from 'classnames';
import sdk from '@/sdk';
import {Popper, PopperTrigger} from '@baidu/luffy-comp-popper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Tooltip, {TooltipTrigger} from '@/components/Tooltip';
import Icon, {IconSize} from '@/components/Icon';
import styles from './index.module.scss';
import {throttle} from 'lodash-es';
import * as store from '@/store';
import {knowledgeCurrencyClick} from '@/buried/home';
import {DOC_URL_SOURCE, EDITOR_MODE} from '@/Constant';
import {
    feLogger,
    longBrowsingTimeReport,
    reportCloseStrengthenCoinGuidance,
    reportDepositStrengthenCoinGuidance,
    reportShowStrengthenCoinGuidance,
} from '@/buried';
import bouncingCoins from '@/assets/img/bouncingCoins.gif';
import StrengthenCoinGuidance from './StrengthenCoinGuidance';
import {getUrlParams, removeSearchParamNoRefresh} from '@/utils/urlUtils';
import eventBus, {DEPOSIT_KNOWLEDGE_COIN} from '@/eventBus';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';


const REFER_INFOFLOW = {
    PC: 'infoflow-pc',
    MOBILE: 'infoflow-mobile',
};
interface IViewKnowledgeCoin {
    repository: Repository;
    icon: React.FunctionComponent;
    curUserIsDepositCurDocument?: boolean;
    number?: number;
    curDocument?: WizDocument;
    editorDomReady?: boolean;
    documentMode: EDITOR_MODE;
}

const MAX_SHOW_ANIMATION = 3;

function ViewKnowledgeCoin(props: IViewKnowledgeCoin) {
    const {
        repository,
        icon,
        number = 0,
        curUserIsDepositCurDocument = false,
        curDocument,
        editorDomReady,
        documentMode,
    } = props;


    const {nickname = 'TA'} = curDocument || {};
    const tooltipMessage = `给${nickname}送个知识币吧～`;
    const isCoinMessage = '您已投过啦，去给其他好文投吧～';
    const knowledegCoinRef = useRef<null | HTMLElement>(null);
    const knowledgeCoinShadowRef = useRef<null | HTMLElement>(null);
    const bottomBarKnowledgeRef = useRef<null | HTMLDivElement>(null);
    const changeScrollTarget = useRef(true);
    const clearPopperTimeoutId = useRef<NodeJS.Timeout>();
    const clearAnimationTimeoutId = useRef<NodeJS.Timeout>();

    const [showAnimation, setShowAnimation] = useState(false);
    const [showAnimationTimes, setShowAnimationTimes] = useState(0);

    const handleScroll = (dom: HTMLElement) => {
        dom.scrollIntoView({
            block: 'center',
        });
        changeScrollTarget.current = !changeScrollTarget.current;
    };


    // 点击知识币的滚动行为
    const clickScrollToKnowledgeCoin = throttle(() => {
        knowledegCoinRef.current = document.getElementById('knowledgeCoinId');
        knowledgeCoinShadowRef.current = document.getElementById('coinIconBgShadow');
        // 当节点和节点shadow存在的时候，才可以进行滚动
        if (knowledegCoinRef.current && knowledgeCoinShadowRef.current) {
            // 当文档没有完全渲染时，无法跳转
            if (!editorDomReady) {
                toast({
                    content: '文档努力加载中，请加载后再赠送知识币~',
                    type: ToastType.Error,
                });
                return;
            }
            const target = changeScrollTarget.current ? knowledegCoinRef.current : knowledgeCoinShadowRef.current;
            handleScroll(target);
            knowledgeCurrencyClick();
            return;
        }
        toast({
            content: '请在文档预览态下给TA赠送知识币~',
            type: ToastType.Error,
        });
    }, 500);

    // 进入文档5秒后，显示知识币微动效，上报埋点
    const executionTimer = () => {
        // eslint-disable-next-line @typescript-eslint/init-declarations
        let timer: NodeJS.Timeout;
        clearAnimationTimeoutId.current = setTimeout(() => {
            clearTimeout(timer);
            setShowAnimation(true);
            feLogger(
                longBrowsingTimeReport()
            );
            // gif图播放完毕后，显示默认icon
            timer = setTimeout(() => {
                setShowAnimation(false);
                setShowAnimationTimes(pre => pre + 1);
            }, 1900);
        }, 5000);
    };

    // 组件卸载时清除定时器
    useEffect(() => {
        return () => {
            clearTimeout(clearAnimationTimeoutId.current);
        };
    }, []);

    // 编辑态时清除定时器，回到预览态重新执行定时任务
    useEffect(() => {
        if (documentMode === EDITOR_MODE.edit) {
            clearTimeout(clearAnimationTimeoutId.current);
        }
        if (documentMode === EDITOR_MODE.preview) {
            if (showAnimationTimes < MAX_SHOW_ANIMATION) {
                executionTimer();
            }
        }
    }, [documentMode, showAnimationTimes]);

    useEffect(() => {
        async function initUserCoinInfo() {
            const ksServer = await sdk.getKsServer(repository);
            const result = await ksServer.getUserCoinInfo(curDocument.docGuid);
            const {source: sourceId} = store.getUrlQuery();
            const {refer} = getUrlParams();
            removeSearchParamNoRefresh('refer');

            const {userGuid, goodArticle, hasRewardedCoin, userCoinInfo} = result;
            const {availableCoinCount, givenCoinCount} = userCoinInfo;
            const isMyself = userGuid === curDocument?.userGuid;
            const canDepositCoin = availableCoinCount > 0;
            const isFromInfoflow = refer === REFER_INFOFLOW.MOBILE || refer === REFER_INFOFLOW.PC;
            const isStrengthen = DOC_URL_SOURCE.includes(Number(sourceId)) || isFromInfoflow;

            if (!isMyself && isStrengthen && goodArticle && !hasRewardedCoin && canDepositCoin) {
                clearPopperTimeoutId.current = setTimeout(() => {
                    clearTimeout(clearAnimationTimeoutId.current);
                    feLogger(reportShowStrengthenCoinGuidance());
                }, 6000);
            }

        }

        initUserCoinInfo();

        return () => {
            clearTimeout(clearPopperTimeoutId.current);
        };
    }, [curDocument.docGuid, curDocument?.userGuid, repository]);

    return (
        <Tooltip
            trigger={TooltipTrigger.Hover}
            content={curUserIsDepositCurDocument ? isCoinMessage : tooltipMessage}
            // beforeChange={handleTooltipChange}
        >
            <div
                className={styles.countNumKnowledgeCoin}
                onClick={clickScrollToKnowledgeCoin}
            >
                <PopperTrigger
                    target={bottomBarKnowledgeRef.current}
                    // visible={isStrengthenShow}
                >
                    <div
                        ref={bottomBarKnowledgeRef}
                        className={classNames(
                            styles.iconContainerWithPopper
                        )}
                    >
                        {/* 只有更新showAnimation时重新加载gif图 */}
                        <img
                            className={classNames(styles.docCoinGif, !showAnimation && styles.hidden)}
                            src={`${bouncingCoins}?v=${showAnimation ? 'start' : 'end'}`}
                        />
                        <Icon
                            className={classNames(styles.iconWithPopover, showAnimation && styles.hidden)}
                            svg={icon}
                            size={IconSize.LG}
                        />
                    </div>
                </PopperTrigger>
                <span>{number}</span>
            </div>
        </Tooltip>
    );
}

export default store.connect([
    store.KEYS.EDITOR_DOM_READY,
    store.KEYS.CUR_USER_IS_DEPOSIT_CUR_DOCUMENT,
])(ViewKnowledgeCoin);

